import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServicePage from "@/pages/ServicePage";
import About from "@/pages/About";
import Insights from "@/pages/Insights";
import InsightArticle from "@/pages/InsightArticle";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudy from "@/pages/CaseStudy";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Cookies from "@/pages/Cookies";
import Terms from "@/pages/Terms";
import DataRetention from "@/pages/DataRetention";
import SubProcessors from "@/pages/SubProcessors";
import Brochure from "@/pages/Brochure";
import Resources from "@/pages/Resources";
import LiteAssessment from "@/pages/LiteAssessment";
import SystemOfProof from "@/pages/SystemOfProof";
import FrameworkLibrary from "@/pages/FrameworkLibrary";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import Docs from "@/pages/Docs";
import Enterprise360 from "@/pages/Enterprise360";
import ValueStream360 from "@/pages/ValueStream360";
import The360Family from "@/pages/The360Family";
import HowItWorks from "@/pages/HowItWorks";
import SystemOfProofInAction from "@/pages/SystemOfProofInAction";
import StartHere from "@/pages/StartHere";
import Methodology from "@/pages/Methodology";
import Auth0Callback from "@/pages/Auth0Callback";
import NotFound from "@/pages/NotFound";
import Help from "@/pages/Help";
import SharedAssessment from "@/pages/SharedAssessment";
import ReviewerPublic from "@/pages/ReviewerPublic";
import ClientSelfAssessment from "@/pages/ClientSelfAssessment";
import ClientPortal from "@/pages/ClientPortal";
import TrialRequest from "@/pages/TrialRequest";
import CampaignLanding from "@/pages/CampaignLanding";
import Accessibility from "@/pages/Accessibility";
import SignIn from "@/pages/SignIn";
import Logout from "@/pages/Logout";
import ProspectTools from "@/pages/ProspectTools";
import CookieConsent from "@/components/CookieConsent";
import SeoHead from "@/components/SeoHead";
import { loadBranding } from "@/lib/branding";
import SsoReentryBanner from "@/components/SsoReentryBanner";
import ResumeAssessmentBanner from "@/components/ResumeAssessmentBanner";
import SearchModal from "@/components/SearchModal";
import HelpWidget from "@/components/HelpWidget";
import ExitIntentModal from "@/components/ExitIntentModal";
import Auth0ProviderWrapper from "@/auth/Auth0ProviderWrapper";
import { initTextZoom } from "@/lib/textZoom";
import { loadBrandAssets, applyBrandFavicon } from "@/lib/brandManifest";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

// Route-level intercept for the Emergent OAuth callback. The session_id arrives
// in the URL fragment, which has to be processed BEFORE any normal route renders
// (so we don't redirect to /admin before the cookie is set).
const AppRouter = () => {
  const location = useLocation();

  // Post-logout closure: ERIO's logout bounces back with ?signed_out=1.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("signed_out") === "1") {
      toast.success("You've been signed out.");
      params.delete("signed_out");
      const qs = params.toString();
      window.history.replaceState({}, "", location.pathname + (qs ? `?${qs}` : ""));
    }
  }, [location.search, location.pathname]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Standalone, chromeless client assessment (no site nav / cookie chrome) */}
      <Route path="/self-assessment/:ref" element={<ClientSelfAssessment />} />
      <Route path="/portal" element={<ClientPortal />} />
      <Route path="/reviewer/:token" element={<ReviewerPublic />} />
      <Route path="*" element={<LayoutRoutes />} />
    </Routes>
  );
};

const LayoutRoutes = () => {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:slug" element={<ServicePage />} />
        <Route path="/enterprise360" element={<Enterprise360 />} />
        <Route path="/system-of-proof" element={<SystemOfProof />} />
        <Route path="/system-of-proof-library" element={<FrameworkLibrary />} />
        <Route path="/value-stream-assessment" element={<ValueStream360 />} />
        <Route path="/the-360-family" element={<The360Family />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/system-of-proof-in-action" element={<SystemOfProofInAction />} />
        <Route path="/start" element={<StartHere />} />
        <Route path="/methodology" element={<Methodology />} />
        <Route path="/about" element={<About />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/insights/:slug" element={<InsightArticle />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/case-studies/:slug" element={<CaseStudy />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/data-retention" element={<DataRetention />} />
        <Route path="/sub-processors" element={<SubProcessors />} />
        <Route path="/help" element={<Help />} />
        <Route path="/shared/:token" element={<SharedAssessment />} />
        <Route path="/trial/request" element={<TrialRequest />} />
        <Route path="/c/:slug" element={<CampaignLanding />} />
        <Route path="/accessibility" element={<Accessibility />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/tools" element={<ProspectTools />} />
        <Route path="/brochure/:slug" element={<Brochure />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/:slug" element={<LiteAssessment />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
};

// Marketing-only overlays (cookie banner, help widget, search, exit-intent, SSO
// banners). Suppressed on the gated /docs portal, which has its own bare shell.
const MarketingOverlays = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/docs") || pathname.startsWith("/self-assessment/") || pathname.startsWith("/portal") || pathname.startsWith("/reviewer/")) return null;
  return (
    <>
      <SsoReentryBanner />
      <ResumeAssessmentBanner />
      <CookieConsent />
      <SearchModal />
      <HelpWidget />
      <ExitIntentModal />
    </>
  );
};

function App() {
  useEffect(() => {
    initTextZoom();
    loadBrandAssets().then(applyBrandFavicon);
    loadBranding().then((b) => {
      if (b?.primary_color) document.documentElement.style.setProperty("--ek-primary", b.primary_color);
    });
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <Auth0ProviderWrapper>
          <ScrollToTop />
          <SeoHead />
          <AppRouter />
          <MarketingOverlays />
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              style: {
                fontFamily: "Work Sans, sans-serif",
              },
            }}
          />
        </Auth0ProviderWrapper>
      </BrowserRouter>
    </div>
  );
}

export default App;
